import { supabase } from '../lib/supabase';
import { invokeAuthed } from './functionAuth';
import { analyticsService } from './analyticsService';
import { buildIntelligenceEngine, generateEvidenceContext, generateDeterministicReview } from '../utils/reviewEvidence';

export const reviewService = {
  getLatestReview: async (userId, periodStart, periodEnd) => {
    const { data, error } = await supabase
      .from('networking_reviews')
      .select('*')
      .eq('user_id', userId)
      .gte('period_start', periodStart.toISOString())
      .lte('period_end', periodEnd.toISOString())
      .order('created_at', { ascending: false })
      .limit(1)
      .single();

    if (error && error.code !== 'PGRST116') throw error;

    if (data) {
      const isFresh = new Date(data.created_at).getTime() > Date.now() - (60 * 60 * 1000);
      return { ...data, isFresh };
    }
    return null;
  },

  generateReview: async (userId, startDate, endDate, objective) => {
    // 1. Get exact deterministic summary
    const summary = await analyticsService.getIntelligenceSummary(startDate, endDate);
    if (!summary) throw new Error("Failed to load intelligence summary.");

    // 2. Build the Deterministic Intelligence Engine locally to structure evidence
    const engine = buildIntelligenceEngine(summary);
    const evidenceSnapshot = generateEvidenceContext(engine);

    let reviewData;
    
    try {
      // 3. Attempt Edge Function (OpenAI) passing Engine outputs instead of raw data
      const { data, error } = await invokeAuthed('review-my-networking', {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        objective
      });

      if (error) {
        // Problems with the user's own OpenAI key are shown, never hidden by a fallback.
        let payload = null;
        try {
          payload = await error.context.json();
        } catch (e) {
          // Not a JSON error body.
        }
        if (payload?.code) {
          const keyErr = new Error(payload.error || 'AI request failed');
          keyErr.code = payload.code;
          throw keyErr;
        }
        throw new Error(error.message || "Edge function failed");
      }

      if (!data || data.error) {
        throw new Error(data?.error || "Edge function failed");
      }
      
      reviewData = data;
    } catch (err) {
      if (err.code && (String(err.code).startsWith('byok_') || err.code === 'rate_limit' || err.code === 'settings_unavailable')) {
        throw err;
      }
      console.warn("Review Edge Function unavailable or failed. Using secure deterministic fallback.", err.message);
      
      // 4. Deterministic Fallback using Engine
      const reviewContent = generateDeterministicReview(summary, objective, engine);
      reviewData = {
        review: reviewContent,
        evidence: evidenceSnapshot,
        summary: summary,
        generation_method: 'deterministic_fallback',
        model: 'local_engine'
      };
    }

    // 5. Save to DB
    const { data: savedReview, error: saveError } = await supabase
      .from('networking_reviews')
      .insert({
        user_id: userId,
        period_start: startDate.toISOString(),
        period_end: endDate.toISOString(),
        networking_objective: objective,
        review_content: reviewData.review,
        evidence_snapshot: reviewData.evidence,
        data_summary_snapshot: reviewData.summary,
        generation_method: reviewData.generation_method,
        model_identifier: reviewData.model
      })
      .select()
      .single();

    if (saveError) throw saveError;
    return { ...savedReview, isFresh: true };
  }
};